import { PromiseClient } from 'hub-http/adapters/promiseClient';
import {
  isErr,
  isOk,
  JsonResult,
  parseJsonResult,
  unwrapErrOrElseThrow,
  unwrapOrElseThrow,
} from './Result';
import Raven from 'raven-js';
import { Metrics } from './Metrics';
import quickFetch from 'quick-fetch';
import http from 'hub-http/clients/apiClient';

export const ErrorTypes = {
  FAILED_VALIDATION_CONTAINS_JSON: 'FAILED_VALIDATION_CONTAINS_JSON',
  FAILED_VALIDATION_INVALID_TYPE: 'FAILED_VALIDATION_INVALID_TYPE',
  FAILED_VALIDATION_MAX_LENGTH: 'FAILED_VALIDATION_MAX_LENGTH',
  FAILED_VALIDATION_NO_SCHEMA_FOR_PREFERENCE:
    'FAILED_VALIDATION_NO_SCHEMA_FOR_PREFERENCE',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ErrorFromServer = {
  type: keyof typeof ErrorTypes;
  message?: string;
};
export type NameValuePair<T> = { name: string; value: T };

export type FetchPreferencesResponseObj<T extends Record<string, string>> = {
  results: Partial<T>;
};

export class PreferencesClient {
  private readonly caller: string;
  private readonly fetchEndpoint: string;
  private readonly writeEndpoint: string;
  private readonly deleteEndpoint: string;
  private httpClient: PromiseClient = http;

  protected constructor(
    caller: string,
    fetchEndpoint: string,
    writeEndpoint: string,
    deleteEndpoint: string
  ) {
    this.caller = caller;
    this.fetchEndpoint = fetchEndpoint;
    this.writeEndpoint = writeEndpoint;
    this.deleteEndpoint = deleteEndpoint;
  }

  private waitForEarlyRequestOrSendNewBatchFetch<T>(
    names: string[],
    earlyRequestKey?: string
  ): Promise<T> {
    const earlyRequest = earlyRequestKey
      ? quickFetch.getRequestStateByName(earlyRequestKey)
      : null;

    const sendNewBatchFetch = () =>
      this.httpClient.post<T>(this.fetchEndpoint, {
        data: { names },
        timeout: 5000,
        query: { caller: this.caller },
      });

    if (earlyRequestKey && earlyRequest) {
      return new Promise<T>((resolve, reject) => {
        earlyRequest.whenFinished((result) => {
          resolve(result as T);
          quickFetch.removeEarlyRequest(earlyRequestKey);
        });
        earlyRequest.onError((xhr: unknown, errorMsg: string) => {
          // retry the request
          // eslint-disable-next-line promise/catch-or-return
          sendNewBatchFetch().then(resolve, reject);
          const error = new Error(errorMsg);
          Metrics.counter('fetch-preference-early-request-failed').increment();
          Raven.captureException(error);
          console.error(error);
          quickFetch.removeEarlyRequest(earlyRequestKey);
        });
      });
    } else {
      return sendNewBatchFetch();
    }
  }

  fetch(
    preferenceName: string,
    defaultValue: string,
    earlyRequestKey?: string
  ): Promise<string> {
    if (defaultValue == null) {
      throw new Error('defaultValue cannot be null or undefined');
    }
    return this.waitForEarlyRequestOrSendNewBatchFetch<
      JsonResult<
        FetchPreferencesResponseObj<Record<typeof preferenceName, string>>,
        ErrorFromServer
      >
    >([preferenceName], earlyRequestKey)
      .then((json) => {
        const result = parseJsonResult(json);
        if (isErr(result)) {
          return defaultValue;
        }
        const { results } = unwrapOrElseThrow(result);

        if (Object.prototype.hasOwnProperty.call(results, preferenceName)) {
          return results[preferenceName]!;
        }
        return defaultValue;
      })
      .catch((error) => {
        Metrics.counter('fetch-preference-threw-error').increment();
        Raven.captureException(error);
        console.error(error);
        return defaultValue;
      });
  }

  batchFetch<KvObject extends Record<string, string>>(
    defaults: KvObject,
    earlyRequestKey?: string
  ): Promise<Record<keyof KvObject, string>> {
    for (const [key, value] of Object.entries(defaults)) {
      if (value == null) {
        throw new Error(
          `default value for preference "${key}" cannot be null or undefined`
        );
      }
    }

    return this.waitForEarlyRequestOrSendNewBatchFetch<
      JsonResult<FetchPreferencesResponseObj<KvObject>, ErrorFromServer>
    >(Object.keys(defaults), earlyRequestKey)
      .then((json) => {
        const result = parseJsonResult(json);

        if (isErr(result)) {
          return defaults;
        }

        const { results } = unwrapOrElseThrow(result);
        return { ...defaults, ...results };
      })
      .catch((error) => {
        Metrics.counter('fetch-preferences-batch-threw-error').increment();
        Raven.captureException(error);
        console.error(error);
        return defaults;
      });
  }

  write(preferenceName: string, value: string): Promise<string> {
    return this.httpClient
      .post<JsonResult<NameValuePair<string>, ErrorFromServer>>(
        this.writeEndpoint,
        {
          data: { name: preferenceName, value },
          query: { caller: this.caller },
        }
      )
      .then((json) => {
        const result = parseJsonResult(json);
        if (isOk(result)) {
          return value;
        }
        const errorFromServer = unwrapErrOrElseThrow(result);
        throw new Error(
          errorFromServer.message
            ? `${errorFromServer.type} - ${errorFromServer.message}`
            : errorFromServer.type
        );
      })
      .catch((error) => {
        Metrics.counter('write-preference-threw-error').increment();
        Raven.captureException(error);
        console.error(error);
        throw error;
      });
  }

  del(preferenceName: string): Promise<string | null> {
    return this.httpClient
      .post<JsonResult<NameValuePair<string> | null, ErrorFromServer>>(
        this.deleteEndpoint,
        {
          data: { name: preferenceName },
          query: { caller: this.caller },
        }
      )
      .then((json) => {
        const result = parseJsonResult(json);
        if (isOk(result)) {
          const deletedPreference = unwrapOrElseThrow(result);
          if (deletedPreference === null) {
            return null;
          }
          return deletedPreference.value;
        }
        const errorFromServer = unwrapErrOrElseThrow(result);
        throw new Error(
          errorFromServer.message
            ? `${errorFromServer.type} - ${errorFromServer.message}`
            : errorFromServer.type
        );
      })
      .catch((error) => {
        Metrics.counter('delete-preference-threw-error').increment();
        Raven.captureException(error);
        console.error(error);
        throw error;
      });
  }

  withHttpClient(client: PromiseClient) {
    this.httpClient = client;
    return this;
  }
}
